import { Clipboard, Download, RotateCcw, RotateCw } from 'lucide-react';
import { usePhoto } from '../context/PhotoContext';
import { useLanguage } from '../i18n';
import type { AspectRatio, BorderUnit, ExportFormat, ExportSizeMode, MetadataPosition } from '../types';
import { ColorControl, Panel, RangeControl, Segmented, Toggle } from './controls';

const shadowPresets = [
  { labelKey: 'inspector.none', value: { enabled: false, strength: 0, blur: 0, offsetX: 0, offsetY: 0 } },
  { labelKey: 'inspector.light', value: { enabled: true, strength: 18, blur: 18, offsetX: 0, offsetY: 6 } },
  { labelKey: 'inspector.medium', value: { enabled: true, strength: 30, blur: 32, offsetX: 0, offsetY: 12 } },
  { labelKey: 'inspector.strong', value: { enabled: true, strength: 46, blur: 48, offsetX: 0, offsetY: 18 } }
];

export function Inspector(): JSX.Element {
  const {
    activePhoto,
    batchPhotos,
    settings,
    exportSettings,
    updateSettings,
    updateExportSettings,
    downloadActive,
    copyActive,
    downloadBatch,
    undo,
    redo,
    canUndo,
    canRedo
  } = usePhoto();
  const { t } = useLanguage();

  return (
    <aside className="border-t border-[color:var(--line)] bg-[color:var(--surface)] lg:h-[calc(100vh-3.5rem)] lg:w-[360px] lg:overflow-y-auto lg:border-l lg:border-t-0">
      <div className="flex items-center justify-between border-b border-[color:var(--line)] px-4 py-3">
        <div>
          <p className="text-sm font-semibold">{activePhoto?.fileName ?? t('inspector.noPhoto')}</p>
          <p className="mt-0.5 text-xs text-[color:var(--muted)]">
            {activePhoto ? `${activePhoto.width} x ${activePhoto.height}` : t('inspector.uploadBegin')}
          </p>
        </div>
        <div className="flex gap-1">
          <button className="icon-button h-9 min-w-9 px-2" disabled={!canUndo} onClick={undo} aria-label={t('inspector.undo')}>
            <RotateCcw size={15} />
          </button>
          <button className="icon-button h-9 min-w-9 px-2" disabled={!canRedo} onClick={redo} aria-label={t('inspector.redo')}>
            <RotateCw size={15} />
          </button>
        </div>
      </div>

      <Panel title={t('inspector.frame')}>
        <Segmented<BorderUnit>
          label={t('inspector.borderUnit')}
          value={settings.borderUnit}
          options={[
            { value: 'percent', label: t('inspector.percent') },
            { value: 'px', label: t('inspector.pixels') }
          ]}
          onChange={(borderUnit) => updateSettings({ borderUnit, borderWidth: borderUnit === 'px' ? 80 : 8 })}
        />
        <RangeControl
          label={t('inspector.borderWidth')}
          value={settings.borderWidth}
          min={0}
          max={settings.borderUnit === 'px' ? 200 : 20}
          suffix={settings.borderUnit === 'px' ? ' px' : '%'}
          onChange={(borderWidth) => updateSettings({ borderWidth })}
        />
        <ColorControl label={t('inspector.borderColor')} value={settings.borderColor} onChange={(borderColor) => updateSettings({ borderColor })} />
        <RangeControl label={t('inspector.opacity')} value={settings.borderOpacity} min={0} max={100} suffix="%" onChange={(borderOpacity) => updateSettings({ borderOpacity })} />
        <Segmented<AspectRatio>
          label={t('inspector.aspectRatio')}
          value={settings.aspectRatio}
          options={[
            { value: 'original', label: t('inspector.original') },
            { value: '1:1', label: '1:1' },
            { value: '4:3', label: '4:3' },
            { value: '16:9', label: '16:9' },
            { value: '3:2', label: '3:2' },
            { value: '2:3', label: '2:3' },
            { value: '9:16', label: '9:16' },
            { value: 'custom', label: t('inspector.custom') }
          ]}
          onChange={(aspectRatio) => updateSettings({ aspectRatio })}
        />
        {settings.aspectRatio === 'custom' ? (
          <div className="grid grid-cols-2 gap-2">
            <label>
              <span className="control-label mb-2 block">{t('inspector.width')}</span>
              <input className="input-field w-full" type="number" min={1} value={settings.customAspectWidth} onChange={(event) => updateSettings({ customAspectWidth: Number(event.target.value) })} />
            </label>
            <label>
              <span className="control-label mb-2 block">{t('inspector.height')}</span>
              <input className="input-field w-full" type="number" min={1} value={settings.customAspectHeight} onChange={(event) => updateSettings({ customAspectHeight: Number(event.target.value) })} />
            </label>
          </div>
        ) : null}
      </Panel>

      <Panel title={t('inspector.shadow')}>
        <Toggle label={t('inspector.enableShadow')} checked={settings.shadow.enabled} onChange={(enabled) => updateSettings({ shadow: { enabled } })} />
        <div className="grid grid-cols-4 gap-1">
          {shadowPresets.map((preset) => (
            <button key={preset.labelKey} className="icon-button h-9 px-2 text-xs" onClick={() => updateSettings({ shadow: preset.value })}>
              {t(preset.labelKey)}
            </button>
          ))}
        </div>
        <RangeControl label={t('inspector.strength')} value={settings.shadow.strength} min={0} max={100} suffix="%" onChange={(strength) => updateSettings({ shadow: { strength, enabled: strength > 0 } })} />
        <RangeControl label={t('inspector.blur')} value={settings.shadow.blur} min={0} max={50} suffix=" px" onChange={(blur) => updateSettings({ shadow: { blur } })} />
        <div className="grid grid-cols-2 gap-3">
          <RangeControl label={t('inspector.offsetX')} value={settings.shadow.offsetX} min={-20} max={20} suffix=" px" onChange={(offsetX) => updateSettings({ shadow: { offsetX } })} />
          <RangeControl label={t('inspector.offsetY')} value={settings.shadow.offsetY} min={-20} max={20} suffix=" px" onChange={(offsetY) => updateSettings({ shadow: { offsetY } })} />
        </div>
        <ColorControl label={t('inspector.shadowColor')} value={settings.shadow.color} onChange={(color) => updateSettings({ shadow: { color } })} />
      </Panel>

      <Panel title={t('inspector.corners')}>
        <Toggle label={t('inspector.roundedCorners')} checked={settings.corners.enabled} onChange={(enabled) => updateSettings({ corners: { enabled } })} />
        <Toggle label={t('inspector.linkCorners')} checked={settings.corners.linked} onChange={(linked) => updateSettings({ corners: { linked } })} />
        {settings.corners.linked ? (
          <RangeControl label={t('inspector.radius')} value={settings.corners.radius} min={0} max={50} suffix=" px" onChange={(radius) => updateSettings({ corners: { radius } })} />
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <RangeControl label={t('inspector.topL')} value={settings.corners.topLeft} min={0} max={50} suffix=" px" onChange={(topLeft) => updateSettings({ corners: { topLeft } })} />
            <RangeControl label={t('inspector.topR')} value={settings.corners.topRight} min={0} max={50} suffix=" px" onChange={(topRight) => updateSettings({ corners: { topRight } })} />
            <RangeControl label={t('inspector.bottomR')} value={settings.corners.bottomRight} min={0} max={50} suffix=" px" onChange={(bottomRight) => updateSettings({ corners: { bottomRight } })} />
            <RangeControl label={t('inspector.bottomL')} value={settings.corners.bottomLeft} min={0} max={50} suffix=" px" onChange={(bottomLeft) => updateSettings({ corners: { bottomLeft } })} />
          </div>
        )}
      </Panel>

      <Panel title={t('inspector.metadata')}>
        <Toggle label={t('inspector.showExif')} checked={settings.metadata.enabled} onChange={(enabled) => updateSettings({ metadata: { enabled } })} />
        <Segmented<MetadataPosition>
          label={t('inspector.position')}
          value={settings.metadata.position}
          options={[
            { value: 'bottom-left', label: t('inspector.bottomL') },
            { value: 'bottom-right', label: t('inspector.bottomR') },
            { value: 'top-left', label: t('inspector.topL') },
            { value: 'top-right', label: t('inspector.topR') }
          ]}
          onChange={(position) => updateSettings({ metadata: { position } })}
        />
        <label>
          <span className="control-label mb-2 block">{t('inspector.customText')}</span>
          <input className="input-field w-full" placeholder={t('inspector.customTextPlaceholder')} value={settings.metadata.customText} onChange={(event) => updateSettings({ metadata: { customText: event.target.value } })} />
        </label>
        <RangeControl label={t('inspector.fontSize')} value={settings.metadata.fontSize} min={12} max={56} suffix=" px" onChange={(fontSize) => updateSettings({ metadata: { fontSize } })} />
        <ColorControl label={t('inspector.textColor')} value={settings.metadata.color} onChange={(color) => updateSettings({ metadata: { color } })} />
        <div className="grid grid-cols-2 gap-2">
          {[
            ['inspector.camera', 'showCamera'],
            ['inspector.lens', 'showLens'],
            ['inspector.exposure', 'showExposure'],
            ['inspector.date', 'showDate'],
            ['inspector.gps', 'showLocation']
          ].map(([labelKey, key]) => (
            <label key={key} className="flex items-center gap-2 rounded-md border border-[color:var(--line)] px-2 py-2 text-xs font-medium">
              <input
                type="checkbox"
                checked={Boolean(settings.metadata[key as keyof typeof settings.metadata])}
                onChange={(event) => updateSettings({ metadata: { [key]: event.target.checked } })}
              />
              {t(labelKey)}
            </label>
          ))}
        </div>
      </Panel>

      <Panel title={t('inspector.exportSection')}>
        <Segmented<ExportFormat>
          label={t('inspector.format')}
          value={exportSettings.format}
          options={[
            { value: 'image/jpeg', label: 'JPG' },
            { value: 'image/png', label: 'PNG' }
          ]}
          onChange={(format) => updateExportSettings({ format })}
        />
        <RangeControl label={t('inspector.quality')} value={exportSettings.quality} min={1} max={100} suffix="%" onChange={(quality) => updateExportSettings({ quality })} />
        <Segmented<ExportSizeMode>
          label={t('inspector.size')}
          value={exportSettings.sizeMode}
          options={[
            { value: 'original', label: t('inspector.original') },
            { value: 'preset', label: t('inspector.preset') },
            { value: 'custom', label: t('inspector.custom') }
          ]}
          onChange={(sizeMode) => updateExportSettings({ sizeMode })}
        />
        {exportSettings.sizeMode === 'preset' ? (
          <Segmented
            label={t('inspector.longEdge')}
            value={String(exportSettings.presetLongEdge)}
            options={[
              { value: '1080', label: '1080' },
              { value: '2048', label: '2048' },
              { value: '4096', label: '4096' }
            ]}
            onChange={(value) => updateExportSettings({ presetLongEdge: Number(value) })}
          />
        ) : null}
        {exportSettings.sizeMode === 'custom' ? (
          <div className="grid grid-cols-2 gap-2">
            <label>
              <span className="control-label mb-2 block">{t('inspector.width')}</span>
              <input className="input-field w-full" type="number" min={1} value={exportSettings.customWidth} onChange={(event) => updateExportSettings({ customWidth: Number(event.target.value) })} />
            </label>
            <label>
              <span className="control-label mb-2 block">{t('inspector.height')}</span>
              <input className="input-field w-full" type="number" min={1} value={exportSettings.customHeight} onChange={(event) => updateExportSettings({ customHeight: Number(event.target.value) })} />
            </label>
          </div>
        ) : null}
        <label>
          <span className="control-label mb-2 block">{t('inspector.fileName')}</span>
          <input className="input-field w-full" placeholder={t('inspector.filePlaceholder')} value={exportSettings.fileName} onChange={(event) => updateExportSettings({ fileName: event.target.value })} />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button className="primary-button" onClick={() => void downloadActive()}>
            <Download size={16} />
            {t('inspector.download')}
          </button>
          <button className="icon-button gap-2" onClick={() => void copyActive()}>
            <Clipboard size={16} />
            {t('inspector.copy')}
          </button>
        </div>
        <button className="icon-button w-full gap-2" onClick={() => void downloadBatch()} disabled={batchPhotos.length <= 1}>
          <Download size={16} />
          {t('inspector.batchExport')} {batchPhotos.length > 1 ? `(${batchPhotos.length})` : ''}
        </button>
      </Panel>
    </aside>
  );
}
